import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, download } from "../lib/api";
import { useMeta, useMetaRefresh, useActiveCategories } from "../lib/meta";
import type { CardTemplate, TemplateImpact, TemplateSpec } from "../lib/types";
import { formatINR } from "../lib/format";
import { Badge, EmptyState, ErrorBanner, Modal, Panel, Spinner } from "./ui";
import { BLANK_SPEC, TemplateEditor } from "./TemplateEditor";
import { ShareModal } from "./ShareModal";

export function TemplatesTab({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const meta = useMeta();
  const refreshMeta = useMetaRefresh();
  const categories = useActiveCategories();
  const [templates, setTemplates] = useState<CardTemplate[] | null>(null);
  const [editing, setEditing] = useState<{ template: CardTemplate | null; spec: TemplateSpec } | null>(null);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState<{ kind: "TEMPLATE"; sourceId: string; title: string } | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [apply, setApply] = useState<{ template: CardTemplate; cards: TemplateImpact[] } | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ templates: CardTemplate[] }>("/templates");
      setTemplates(r.templates);
      await refreshMeta();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load templates");
    }
  }, [onError, refreshMeta]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (!editing) return;
    setSaving(true);
    setEditError(null);
    try {
      const saved = editing.template
        ? await api.put<{ template: CardTemplate }>(`/templates/${editing.template.id}`, { spec: editing.spec })
        : await api.post<{ template: CardTemplate }>("/templates", { spec: editing.spec });

      const id = saved.template.id;
      setEditing(null);
      await load();

      // Nothing reaches an existing card until you say so.
      const { cards } = await api.get<{ cards: TemplateImpact[] }>(`/templates/${id}/impact`);
      if (cards.length > 0) {
        const fresh = await api.get<{ templates: CardTemplate[] }>("/templates");
        const template = fresh.templates.find((t) => t.id === id);
        if (template) setApply({ template, cards });
      } else {
        onNotice("Template saved.");
      }
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : "Could not save the template");
    } finally {
      setSaving(false);
    }
  }

  async function reset(t: CardTemplate) {
    if (!confirm(`Put "${t.spec.name}" back to the version that ships with the app?`)) return;
    try {
      await api.post(`/templates/${t.id}/reset`);
      onNotice(`"${t.spec.name}" reset.`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not reset the template");
    }
  }

  async function remove(t: CardTemplate) {
    if (!confirm(`Delete the "${t.spec.name}" template? Cards already created from it are not affected.`)) return;
    try {
      await api.del(`/templates/${t.id}`);
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete the template");
    }
  }

  async function restoreBuiltins() {
    try {
      const r = await api.post<{ restored: number }>("/templates/restore-builtins");
      onNotice(r.restored > 0 ? `Added ${r.restored} built-in template${r.restored === 1 ? "" : "s"}.` : "You already have every built-in template.");
      await load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not restore");
    }
  }

  if (!templates || !meta) return <Spinner />;

  return (
    <>
      <Panel
        title="Card templates"
        subtitle="The starting rates, caps and milestones used when you add a card. Edit them as issuers change terms."
        actions={
          <div className="flex flex-wrap gap-2">
            <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => void download("/templates/export", "card-templates.json")}>
              Export JSON
            </button>
            <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setImportOpen(true)}>Import JSON</button>
            <button className="btn-secondary px-3 py-1.5 text-xs" onClick={restoreBuiltins}>Add missing built-ins</button>
            <button className="btn-primary px-3 py-1.5 text-xs" onClick={() => setEditing({ template: null, spec: structuredClone(BLANK_SPEC) })}>
              New template
            </button>
          </div>
        }
      >
        {templates.length === 0 ? (
          <EmptyState
            title="No templates"
            hint="Add the built-in set, or create your own."
            action={<button className="btn-primary" onClick={restoreBuiltins}>Add missing built-ins</button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ink-200">
                  <th className="th">Card</th>
                  <th className="th text-right">Annual fee</th>
                  <th className="th text-right">Rules</th>
                  <th className="th text-right">Milestones</th>
                  <th className="th text-right">Your cards</th>
                  <th className="th"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td className="td">
                      <span className="font-medium text-ink-900">{t.spec.name}</span>
                      {t.isModified && <Badge tone="info">Edited</Badge>}
                      {!t.isBuiltIn && <Badge>Custom</Badge>}
                      <span className="block text-xs text-ink-500">{t.spec.issuer} · {t.spec.currency.name}</span>
                    </td>
                    <td className="td tnum text-right">{t.spec.annualFee === 0 ? "Free" : formatINR(t.spec.annualFee)}</td>
                    <td className="td tnum text-right text-ink-600">{t.spec.rules.length}</td>
                    <td className="td tnum text-right text-ink-600">{t.spec.milestones.length}</td>
                    <td className="td tnum text-right text-ink-600">{t.cardCount}</td>
                    <td className="td whitespace-nowrap text-right">
                      <button className="text-xs text-accent-700 underline"
                        onClick={() => setEditing({ template: t, spec: structuredClone(t.spec) })}>
                        Edit
                      </button>
                      {t.isBuiltIn && t.isModified && (
                        <button className="ml-3 text-xs text-ink-600 underline" onClick={() => reset(t)}>Reset</button>
                      )}
                      <button className="ml-3 text-xs text-accent-700 underline"
                        onClick={() => setSharing({ kind: "TEMPLATE", sourceId: t.id, title: t.spec.name })}>
                        Share
                      </button>
                      <button className="ml-3 text-xs text-red-700 underline" onClick={() => remove(t)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 space-y-1 text-xs text-ink-500">
          <p>
            Editing a template never changes a card on its own — after saving you're asked whether to push the
            change to cards made from it, and shown exactly what would change first.
          </p>
          <p>
            Export gives you every template as one JSON file. Correct it in a text editor and import it back to
            update the whole set in one pass.
          </p>
        </div>
      </Panel>

      {/* ----------------------------------------------------- edit modal */}
      <Modal
        open={editing != null}
        onClose={() => { setEditing(null); setEditError(null); }}
        title={editing?.template ? `Edit "${editing.template.spec.name}"` : "New card template"}
        wide
        footer={
          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={() => { setEditing(null); setEditError(null); }}>Cancel</button>
            <button className="btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save template"}
            </button>
          </div>
        }
      >
        {editing && (
          <>
            <TemplateEditor
              spec={editing.spec}
              onChange={(spec) => setEditing({ ...editing, spec })}
              categories={categories}
              networks={meta.networks}
            />
            {editError && <div className="mt-4"><ErrorBanner message={editError} onDismiss={() => setEditError(null)} /></div>}
          </>
        )}
      </Modal>

      <ApplyModal
        state={apply}
        onClose={() => { setApply(null); onNotice("Template saved. Your cards were left as they are."); }}
        onApplied={(n) => {
          setApply(null);
          onNotice(`Template saved and pushed to ${n} card${n === 1 ? "" : "s"}. Recalculate points on the Transactions page to re-score your history.`);
          void load();
        }}
        onError={onError}
      />

      <ImportModal
        open={importOpen}
        fileInput={fileInput}
        onClose={() => setImportOpen(false)}
        onDone={(msg) => { setImportOpen(false); onNotice(msg); void load(); }}
      />
      <ShareModal target={sharing} onClose={() => setSharing(null)} onShared={(m) => { setSharing(null); onNotice(m); }} />
    </>
  );
}

/* ------------------------------------------------- push to existing cards */

function ApplyModal({
  state,
  onClose,
  onApplied,
  onError,
}: {
  state: { template: CardTemplate; cards: TemplateImpact[] } | null;
  onClose: () => void;
  onApplied: (count: number) => void;
  onError: (m: string) => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSelected(state ? state.cards.map((c) => c.cardId) : []);
  }, [state]);

  if (!state) return null;

  async function run() {
    if (!state) return;
    setBusy(true);
    try {
      const r = await api.post<{ updated: number }>(`/templates/${state.template.id}/apply`, { cardIds: selected });
      onApplied(r.updated);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not update your cards");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open title="Push this change to your cards?" onClose={onClose} wide>
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          You edited <strong>{state.template.spec.name}</strong>. These cards were created from it. Updating a card
          replaces its earn rules, milestones and fee figures with the template's.
        </p>
        <p className="rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-xs text-ink-600">
          Your own settings are kept: the name you gave the card, statement and due days, anniversary, last four
          digits, colour, credit limit and opening points balance.
        </p>

        <ul className="space-y-3">
          {state.cards.map((c) => (
            <li key={c.cardId} className="rounded-lg border border-ink-200 p-3">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
                  checked={selected.includes(c.cardId)}
                  onChange={(e) =>
                    setSelected(e.target.checked ? [...selected, c.cardId] : selected.filter((id) => id !== c.cardId))
                  }
                />
                <span className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-ink-900">{c.cardName}</span>
                  <span className="mt-1 block text-xs text-ink-600">
                    Earn rules {c.rulesBefore} → {c.rulesAfter} · milestones {c.milestonesBefore} → {c.milestonesAfter}
                  </span>
                  {c.fieldChanges.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {c.fieldChanges.map((f) => (
                        <li key={f.field} className="tnum text-xs text-amber-700">
                          {f.field}: {f.from} → {f.to}
                        </li>
                      ))}
                    </ul>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>

        <p className="mb-3 text-xs text-ink-500">
          Updating replaces every earn rule on the card, earlier dated versions included, and the template's rules
          then apply to every date. If the bank changed its terms from a date, edit the rule on the card instead and
          tick "These changes start on a date".
        </p>

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Leave my cards alone</button>
          <button className="btn-primary" onClick={run} disabled={busy || selected.length === 0}>
            {busy ? "Updating…" : `Update ${selected.length} card${selected.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------- import modal */

function ImportModal({
  open,
  fileInput,
  onClose,
  onDone,
}: {
  open: boolean;
  fileInput: React.RefObject<HTMLInputElement>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [text, setText] = useState("");
  const [mode, setMode] = useState<"replace" | "skip" | "duplicate">("replace");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function run() {
    setProblem(null);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setProblem("That isn't valid JSON");
      return;
    }
    const templates = Array.isArray(parsed)
      ? parsed
      : (parsed as { templates?: unknown[] })?.templates;
    if (!Array.isArray(templates)) {
      setProblem("Expected an exported file, or a plain array of templates");
      return;
    }

    setBusy(true);
    try {
      const r = await api.post<{ created: number; updated: number; skipped: number }>("/templates/import", {
        templates,
        mode,
      });
      setText("");
      onDone(`Imported: ${r.created} new, ${r.updated} updated, ${r.skipped} skipped.`);
    } catch (err) {
      // Validation failures name the offending template, so surface them here.
      setProblem(err instanceof ApiError ? err.message : "Import failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Import card templates" wide>
      <div className="space-y-4">
        <div>
          <label className="label">Paste an exported file, or choose one</label>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="input mb-2"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) setText(await file.text());
            }}
          />
          <textarea
            className="input h-56 font-mono text-xs"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder='{ "templates": [ { "key": "hdfc-infinia", "name": "...", "rules": [...] } ] }'
            spellCheck={false}
          />
        </div>

        <div>
          <label className="label" htmlFor="imp-mode">When a template with the same key already exists</label>
          <select id="imp-mode" className="input" value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
            <option value="replace">Replace it</option>
            <option value="skip">Leave mine alone</option>
            <option value="duplicate">Keep both</option>
          </select>
        </div>

        <p className="text-xs text-ink-500">
          Every template is checked before anything is written — if one entry is wrong, nothing is imported and
          the message says which.
        </p>

        {problem && <ErrorBanner message={problem} onDismiss={() => setProblem(null)} />}

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={run} disabled={busy || !text.trim()}>
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
